/**
 * InkMerge Upload Field — storefront client.
 *
 * Original UI built for InkMerge brand (Inter / Fraunces, sharp corners,
 * teal accent). Drives the same pipeline our Worker expects:
 *
 *   1. FilePond receives the file (drag-drop / click).
 *   2. Client extracts dimensions: <img>.naturalWidth/Height for images;
 *      PDF.js for PDFs (page count + first-page mediabox).
 *   3. POST <proxy>/register  → fileId + upload URL
 *   4. PUT  <upload URL>      → binary streams through Worker into Dropbox
 *   5. PUT  <proxy>/cartline  → finalize, returns JWT + adjustment
 *   6. Render: thumbnail, dimensions, formula, "+$X.XX" adjustment.
 *   7. On submit, inject hidden inputs into the product form:
 *        properties[View uploads], properties[__ucExp], properties[__ucToken]
 *
 * FilePond + PDF.js are CDN-loaded on first need.
 */

(function () {
  "use strict";

  const MAX_FILE_BYTES = 25 * 1024 * 1024;
  const PROXY_PREFIX = "/a/upload-center";
  const FP_CSS = "https://unpkg.com/filepond@4.32.9/dist/filepond.min.css";
  const FP_JS = "https://unpkg.com/filepond@4.32.9/dist/filepond.min.js";
  const PDFJS = "https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.min.mjs";
  const PDFJS_WORKER =
    "https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs";
  const PRODUCT_FIELDS = {
    "9457167663398": {
      id: "a854ecd5-d60a-487f-8adb-75ff95d8d753",
      accepted:
        "image/png,application/pdf,image/svg+xml,image/tiff,application/postscript",
      maxFiles: 1,
    },
    "9457281368358": {
      id: "9a2ad5d2-7853-49db-8c0b-086fa89f9d68",
      accepted: "image/png",
      maxFiles: 1,
    },
  };

  function resolveFieldConfig(root) {
    const productId = String(root.dataset.productId || "");
    const productField = PRODUCT_FIELDS[productId];
    const configs = window.__inkmergeFieldConfig || {};
    const mountedFieldId = String(root.dataset.fieldId || "");
    const mountedConfig =
      configs[mountedFieldId] ||
      Object.values(configs).find(
        (config) => String(config && config.productId) === productId,
      ) ||
      {};

    if (!productField) {
      return { fieldId: mountedFieldId, config: mountedConfig };
    }

    const config = {
      ...mountedConfig,
      fieldId: productField.id,
      accepted: productField.accepted,
      maxFiles: productField.maxFiles,
      required: true,
    };
    window.__inkmergeFieldConfig = configs;
    configs[productField.id] = config;
    root.dataset.fieldId = productField.id;
    root.dataset.maxFiles = String(productField.maxFiles);
    root.dataset.multiple = String(productField.maxFiles > 1);
    root.dataset.required = "true";
    return { fieldId: productField.id, config };
  }

  function acceptedTypes(config) {
    return String(config.accepted || "")
      .split(",")
      .map((type) => type.trim())
      .filter(Boolean);
  }

  function validateFile(file, config) {
    if (!file || !Number.isFinite(file.size) || file.size <= 0) {
      throw new Error("Choose a non-empty artwork file.");
    }
    if (file.size > MAX_FILE_BYTES) {
      throw new Error("File exceeds the 25MB upload limit.");
    }
    const allowed = acceptedTypes(config);
    if (allowed.length && !allowed.includes(file.type)) {
      throw new Error("That file format is not accepted for this product.");
    }
  }

  function loadCss(href) {
    return new Promise((resolve, reject) => {
      if ([...document.styleSheets].some((s) => s.href === href))
        return resolve();
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if ([...document.scripts].some((s) => s.src === src)) return resolve();
      const s = document.createElement("script");
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  let pdfjs;
  async function loadPdfJs() {
    if (pdfjs) return pdfjs;
    pdfjs = await import(PDFJS);
    pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    return pdfjs;
  }

  async function imageDim(file) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const dpiX = 72,
          dpiY = 72;
        const dims = {
          width: img.naturalWidth,
          height: img.naturalHeight,
          inchX: (img.naturalWidth / dpiX).toFixed(2),
          inchY: (img.naturalHeight / dpiY).toFixed(2),
          dpiX,
          dpiY,
          dpi: { x: dpiX, y: dpiY },
          previewUrl: url, // keep for thumbnail
        };
        resolve(dims);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }
  async function pdfDim(file) {
    try {
      const lib = await loadPdfJs();
      const buf = await file.arrayBuffer();
      const pdf = await lib.getDocument({ data: buf }).promise;
      const first = await pdf.getPage(1);
      const viewport = first.getViewport({ scale: 1 });
      const dpiX = 72,
        dpiY = 72;
      // Render first page to a thumbnail canvas for preview
      const scale = Math.min(800 / viewport.width, 800 / viewport.height);
      const thumbViewport = first.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(thumbViewport.width);
      canvas.height = Math.round(thumbViewport.height);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await first.render({ canvasContext: ctx, viewport: thumbViewport })
        .promise;
      return {
        width: Math.round(viewport.width),
        height: Math.round(viewport.height),
        inchX: (viewport.width / dpiX).toFixed(2),
        inchY: (viewport.height / dpiY).toFixed(2),
        dpiX,
        dpiY,
        dpi: { x: dpiX, y: dpiY },
        pageCount: pdf.numPages,
        previewUrl: canvas.toDataURL("image/png"),
      };
    } catch (err) {
      console.warn("[inkmerge] PDF parse failed", err);
      return null;
    }
  }
  async function extractDimensions(file) {
    if (file.type.startsWith("image/")) return imageDim(file);
    if (file.type === "application/pdf") return pdfDim(file);
    return null;
  }

  function createState(fieldId, cfg) {
    return {
      fieldId,
      cfg,
      cartLineId: null,
      files: [], // {fileId, fileName, dim, size, type, registered}
      currentVariantId: cfg.currentVariantId,
      quantity: 1,
      lastAdjustment: 0,
      lastToken: null,
      lastExp: null,
      pendingUploads: new Set(),
    };
  }

  async function registerFile(state, file) {
    validateFile(file, state.cfg);
    const res = await fetch(PROXY_PREFIX + "/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fileName: file.name,
        fileSize: String(file.size),
        contentType: file.type,
        fieldId: state.fieldId,
        cartLineId: state.cartLineId,
      }),
    });
    if (!res.ok) throw new Error("register failed (" + res.status + ")");
    const json = await res.json();
    if (json.message !== "success")
      throw new Error("register: " + (json.error || "unknown"));
    state.cartLineId = json.data.cartLineId;
    return json.data;
  }
  async function uploadBytes(uploadUrl, file) {
    validateFile(file, { accepted: file.type });
    const res = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!res.ok) throw new Error("upload failed (" + res.status + ")");
    return res.json();
  }
  async function finalizeCartline(state, fileEntry) {
    const body = {
      id: fileEntry.fileId,
      fileName: fileEntry.fileName,
      fileSize: fileEntry.size,
      contentType: fileEntry.type,
      fieldId: state.fieldId,
      cartLineId: state.cartLineId,
      quantity: state.quantity,
      variantId: String(state.currentVariantId),
    };
    if (fileEntry.dim) {
      body.dimensions = {
        width: fileEntry.dim.width,
        height: fileEntry.dim.height,
        inchX: fileEntry.dim.inchX,
        inchY: fileEntry.dim.inchY,
        dpiX: fileEntry.dim.dpiX,
        dpiY: fileEntry.dim.dpiY,
        dpi: fileEntry.dim.dpi,
        pageCount: fileEntry.dim.pageCount,
      };
    }
    const res = await fetch(PROXY_PREFIX + "/cartline", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("cartline failed (" + res.status + ")");
    const json = await res.json();
    if (json.message !== "success")
      throw new Error("cartline: " + (json.error || "unknown"));
    state.lastAdjustment = json.data.adjustment;
    state.lastToken = json.data.token;
    state.lastExp = json.data.cartLineExp;
    return json.data;
  }

  function formatBytes(b) {
    if (!b) return "—";
    if (b < 1024) return b + " B";
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
    return (b / 1024 / 1024).toFixed(1) + " MB";
  }
  function formatType(mime) {
    return (
      {
        "image/png": "PNG",
        "image/jpeg": "JPEG",
        "application/pdf": "PDF",
        "image/svg+xml": "SVG",
        "image/tiff": "TIFF",
        "application/postscript": "AI",
      }[mime] || mime
    );
  }
  function unitLabel(t) {
    return (
      {
        INCH_H: "height (inches)",
        INCH_W: "width (inches)",
        INCH_SQ: "square inches",
        CM_H: "height (cm)",
        CM_W: "width (cm)",
        CM_SQ: "square cm",
        PX_H: "height (pixels)",
        PX_W: "width (pixels)",
        PX_SQ: "square pixels",
        PDF_PAGES: "PDF pages",
      }[t] || t
    );
  }

  function setStatus(root, text, state) {
    const el = root.querySelector("[data-status]");
    if (!el) return;
    el.textContent = text;
    if (state) el.setAttribute("data-state", state);
    else el.removeAttribute("data-state");
  }
  function setProductStatus(root, text) {
    const status = root
      .closest("[data-imupload]")
      ?.querySelector("[data-imupload-status]");
    if (status) status.textContent = text || "";
  }
  function showPanel(root, on) {
    const p = root.querySelector("[data-design-panel]");
    if (p) p.hidden = !on;
  }
  function renderMeta(root, dim, file) {
    if (!dim) {
      root.querySelector("[data-meta-dim]").textContent = "—";
      root.querySelector("[data-meta-dpi]").textContent = "—";
      root.querySelector("[data-pages-row]").hidden = true;
    } else {
      root.querySelector("[data-meta-dim]").textContent =
        `${dim.inchX}″ × ${dim.inchY}″`;
      root.querySelector("[data-meta-dpi]").textContent = `${dim.dpiX} DPI`;
      const pagesRow = root.querySelector("[data-pages-row]");
      if (dim.pageCount) {
        pagesRow.hidden = false;
        root.querySelector("[data-meta-pages]").textContent = String(
          dim.pageCount,
        );
      } else {
        pagesRow.hidden = true;
      }
    }
    root.querySelector("[data-meta-size]").textContent = formatBytes(file.size);
    root.querySelector("[data-meta-format]").textContent = formatType(
      file.type,
    );
  }
  function renderThumb(root, previewUrl) {
    const img = root.querySelector("[data-thumb]");
    const ph = root.querySelector("[data-thumb-placeholder]");
    if (previewUrl) {
      img.src = previewUrl;
      img.hidden = false;
      ph.style.display = "none";
    } else {
      img.hidden = true;
      ph.style.display = "";
    }
  }
  function renderPrice(root, adjustment, breakdown, fieldCfg) {
    const row = root.querySelector("[data-price-row]");
    if (!fieldCfg.customPricing || !adjustment) {
      row.hidden = true;
      return;
    }
    row.hidden = false;
    root.querySelector("[data-price-amount]").textContent =
      "+$" + Number(adjustment).toFixed(2);
    const first = breakdown && breakdown[0];
    if (first) {
      root.querySelector("[data-price-formula]").textContent =
        `${first.unitMeasurement?.toFixed?.(2) ?? ""} ${unitLabel(fieldCfg.unitType)} × $${(first.pricePerUnit / (first.unitMeasurement || 1)).toFixed(2)}`;
    } else {
      root.querySelector("[data-price-formula]").textContent = "";
    }
  }
  function renderRules(root, cfg, fieldCfg) {
    const types = acceptedTypes(cfg)
      .map((t) => formatType(t))
      .join(", ");
    root.querySelector("[data-rules-types]").textContent = `Accepts ${types}`;
    root.querySelector("[data-rules-size]").textContent = "Up to 25MB";
    root.querySelector("[data-rules-count]").textContent =
      cfg.maxFiles > 1 ? `1 to ${cfg.maxFiles} files` : "1 file";
  }

  function findProductForm(root) {
    return (
      root.closest("[data-imupload]")?.querySelector("[data-imupload-form]") ||
      root.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector("product-form form") ||
      document.querySelector('form[id^="product-form"]')
    );
  }
  function setProperty(form, key, value) {
    let input = form.querySelector(`input[name="properties[${cssEsc(key)}]"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = `properties[${key}]`;
      form.appendChild(input);
    }
    input.value = value;
  }
  function cssEsc(s) {
    return s.replace(/[\\"]/g, "\\$&");
  }

  async function init(root) {
    const resolved = resolveFieldConfig(root);
    const fieldId = resolved.fieldId;
    const cfg = resolved.config;
    if (!fieldId || !cfg || !cfg.fieldId) {
      setStatus(root, "Upload field not configured", "error");
      return;
    }
    setProductStatus(root, "");

    // Fetch field config server-side (so we know customPricing, unitType, etc.)
    let fieldCfg;
    try {
      // The block doesn't have admin token — fetch only the public-safe subset
      // is exposed under /a/upload-center/field-info — for now infer from page
      fieldCfg = {
        customPricing: false, // default off; storefront learns true via cartline response
        unitType: "INCH_H",
        unitPrice: 0,
      };
    } catch {
      fieldCfg = {};
    }

    renderRules(root, cfg, fieldCfg);

    await loadCss(FP_CSS);
    await loadScript(FP_JS);

    const input = root.querySelector("input.im-field");
    const accepted = acceptedTypes(cfg);

    // Mount FilePond inside the dedicated div so our wrapper sizes it
    const mount = root.querySelector("[data-pond-mount]");
    const dropwrap = root.querySelector(".im-upload__dropwrap");
    mount.appendChild(input);
    root.addEventListener(
      "change",
      (event) => {
        const fileInput = event.target;
        if (!fileInput.matches || !fileInput.matches('input[type="file"]')) return;
        const file = fileInput.files && fileInput.files[0];
        if (!file) return;
        try {
          validateFile(file, cfg);
        } catch (validationError) {
          event.preventDefault();
          event.stopImmediatePropagation();
          fileInput.value = "";
          setStatus(root, validationError.message, "error");
          setProductStatus(root, validationError.message);
        }
      },
      true,
    );
    const pond = window.FilePond.create(input, {
      allowMultiple: cfg.maxFiles > 1,
      maxFiles: cfg.maxFiles,
      acceptedFileTypes: accepted,
      maxFileSize: "25MB",
      instantUpload: false,
      credits: false,
      labelIdle:
        cfg.maxFiles > 1
          ? 'Drag your designs here or <span class="filepond--label-action">browse</span>'
          : 'Drag your design here or <span class="filepond--label-action">browse</span>',
    });

    const state = createState(fieldId, cfg);

    // Track Shopify variant changes
    const sectionRoot = root.closest("[data-imupload]");
    const variantInput =
      sectionRoot?.querySelector("[data-imupload-variant]") ||
      document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (variantInput) {
      state.currentVariantId = Number(variantInput.value);
      new MutationObserver(async () => {
        state.currentVariantId = Number(variantInput.value);
        // Recompute pricing for the most recently uploaded file
        const last = state.files[state.files.length - 1];
        if (last) {
          try {
            const d = await finalizeCartline(state, last);
            renderPrice(root, d.adjustment, d.breakdown, {
              ...fieldCfg,
              ...detectFieldCfg(d),
            });
          } catch {}
        }
      }).observe(variantInput, {
        attributes: true,
        attributeFilter: ["value"],
      });
    }

    // Qty
    const qtyInput =
      sectionRoot?.querySelector("[data-imupload-quantity]") ||
      document.querySelector(
        'form[action*="/cart/add"] input[name="quantity"], form[action*="/cart/add"] input[type="number"]',
      );
    if (qtyInput) {
      qtyInput.addEventListener("change", () => {
        state.quantity = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      });
    }

    pond.on("addfile", async (err, item) => {
      if (err) {
        setStatus(
          root,
          err.body || err.main || "We couldn't accept that file",
          "error",
        );
        return;
      }

      const uploadKey = String(item.id || `${item.filename}:${item.file.size}`);
      if (state.pendingUploads.has(uploadKey)) return;

      try {
        validateFile(item.file, cfg);
      } catch (validationError) {
        setStatus(root, validationError.message, "error");
        try {
          await pond.removeFile(item.id);
        } catch {}
        return;
      }

      state.pendingUploads.add(uploadKey);
      if (dropwrap) dropwrap.classList.add("has-files");
      setStatus(root, `Reading ${item.filename}…`, "busy");
      showPanel(root, true);
      const dim = await extractDimensions(item.file);
      renderMeta(root, dim, item.file);
      renderThumb(root, dim?.previewUrl || null);

      try {
        setStatus(root, "Uploading…", "busy");
        const reg = await registerFile(state, item.file);
        await uploadBytes(reg.url, item.file);

        const entry = {
          pondId: item.id,
          fileId: reg.fileId,
          fileName: item.filename,
          size: item.file.size,
          type: item.file.type,
          dim,
        };
        state.files.push(entry);

        setStatus(root, "Calculating price…", "busy");
        const finalized = await finalizeCartline(state, entry);
        const learned = detectFieldCfg(finalized);
        renderPrice(root, finalized.adjustment, finalized.breakdown, {
          ...fieldCfg,
          ...learned,
        });
        setStatus(root, "Design ready", "success");
        setProductStatus(root, "");
      } catch (e) {
        console.error("[inkmerge]", e);
        setStatus(root, "Upload failed: " + e.message, "error");
        setProductStatus(
          root,
          "Artwork could not be uploaded. This item was not added to the cart.",
        );
      } finally {
        state.pendingUploads.delete(uploadKey);
      }
    });

    pond.on("removefile", (_err, item) => {
      state.files = state.files.filter((f) => f.pondId !== item.id);
      if (dropwrap) {
        dropwrap.classList.toggle("has-files", pond.getFiles().length > 0);
      }
      if (state.files.length === 0) {
        showPanel(root, false);
        setStatus(root, "", null);
      }
    });

    // Intercept add-to-cart submit
    const form = findProductForm(root);
    if (!form) return;
    form.addEventListener(
      "submit",
      (e) => {
        if (cfg.required && state.files.length === 0) {
          e.preventDefault();
          setStatus(
            root,
            "Please upload a design before adding to cart",
            "warn",
          );
          root.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        if (state.lastToken && state.cartLineId) {
          const viewUrl =
            window.location.origin +
            PROXY_PREFIX +
            "/uploads?cartlid=" +
            state.cartLineId;
          setProperty(form, "View uploads", viewUrl);
          setProperty(form, "__ucExp", String(state.lastExp ?? ""));
          setProperty(form, "__ucToken", state.lastToken);
        }
      },
      true,
    );
  }

  /** Sniff field config from a cartline response payload. */
  function detectFieldCfg(finalized) {
    const first = finalized.breakdown && finalized.breakdown[0];
    if (!first || !first.unitMeasurement) return { customPricing: false };
    return { customPricing: true };
  }

  function bootstrap() {
    document.querySelectorAll(".im-upload").forEach((root) => {
      if (root.dataset.inited === "true") return;
      root.dataset.inited = "true";
      init(root).catch((e) => console.error("[inkmerge] init failed", e));
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
  } else {
    bootstrap();
  }
})();
